import fileService from "@services/common/fileService"
import contractEventService, {
  ContractEventImportKind,
} from "@services/contract/contractEventService"
import type { IExportContractEventConvertFilter } from "@services/contract/contractEventService"
import type {
  IContractEventFeeBatchResult,
  IPinkBookDueDateBatchInput,
  IPinkBookPhaseInfo,
} from "@models/contract/contractEventPinkBookFeeModel"
import contractService from "@services/contract/contractService"
import type { PagedResultDto } from "@services/dto/pagedResultDto"
import { action, makeAutoObservable, observable } from "mobx"

class ContractEventStore {
  @observable isLoading!: boolean
  @observable tableData!: PagedResultDto<any>
  @observable detailData!: any
  @observable detailHistoryContract!: any
  @observable printReviewData!: any
  @observable isLoadingPrint!: boolean
  @observable totalPaidAmount!: number
  /**
   * Feature-0204 — danh sách hợp đồng cho bước chọn của form bulk "Cập nhật hạn đợt cấp sổ hồng".
   * Cố ý KHÔNG dùng `contractStore.tableData`: tab Hợp đồng vẫn mount khi mở drawer ở tab Sự kiện,
   * ghi vào đó là đổi luôn dữ liệu người dùng đang xem ở tab kia.
   */
  @observable contractSelectionData!: PagedResultDto<any>
  @observable isLoadingContractSelection!: boolean

  constructor() {
    makeAutoObservable(this)

    this.tableData = { items: [], totalCount: 0 }
    this.detailData = {}
    this.detailHistoryContract = {}
    this.printReviewData = ""
    this.isLoadingPrint = false
    this.totalPaidAmount = 0
    this.contractSelectionData = { items: [], totalCount: 0 }
    this.isLoadingContractSelection = false
  }

  @action
  async get(id: number) {
    this.isLoading = true
    const result = await contractEventService
      .get(id)
      .finally(() => (this.isLoading = false))
    this.detailData = result
  }

  @action
  async getAll(params: any) {
    this.isLoading = true
    const result = await contractEventService
      .getAll(params)
      .finally(() => (this.isLoading = false))
    this.tableData = result
  }

  @action
  async getHistoryContract(id: any) {
    this.isLoading = true
    try {
      this.detailHistoryContract = await contractEventService.getHistoryContract(
        id
      )
    } catch (error: unknown) {
      // Không giữ lại snapshot của sự kiện trước: caller mở modal ngay sau khi await,
      // giữ dữ liệu cũ sẽ hiện hợp đồng của sự kiện khác.
      this.detailHistoryContract = {}
      throw error
    } finally {
      this.isLoading = false
    }
  }

  // CONTRACT EVENT
  @action
  async initData() {
    this.detailData = {}
  }
  async createOrUpdate(body: any, files?: any[], notNotify?: boolean) {
    this.isLoading = true
    // Không nuốt lỗi: trước đây `.catch()` trả về undefined rồi destructure ngay dòng dưới ⇒
    // "Cannot destructure property 'uniqueId' of undefined" che mất lỗi thật của API.
    let result: any
    try {
      result = await contractEventService.createOrUpdate(body, notNotify)
    } catch (error: unknown) {
      this.isLoading = false
      throw error
    }

    const uniqueId = result?.uniqueId
    if (files && files.length && uniqueId) {
      const filesBody = {
        moduleName: "ContractEvent",
        uniqueId: uniqueId,
        descriptions: [...files].map((item) => item?.description),
      }

      await fileService.uploadDocumentWithModuleName(
        [...files].map((item) => item?.file),
        filesBody
      )
    }

    this.isLoading = false
    return result
  }

  /** Feature-0381 — cập nhật riêng khách hàng sự kiện chuyển nhượng. */
  @action
  async updateEventCustomers(contractEventId: number, customers: any[]) {
    this.isLoading = true
    try {
      const result = await contractEventService.updateEventCustomers(
        contractEventId,
        customers
      )
      this.detailData = result
      return result
    } finally {
      this.isLoading = false
    }
  }

  /**
   * Feature-0377 — PHẢI return giá trị của service: `false` là ca BE trả 204 (không có dòng nào),
   * `await` rồi bỏ thì scene không biết để hiện cảnh báo và người dùng nghĩ đã xuất xong.
   */
  @action
  public exportConvertEvents = async (
    params: IExportContractEventConvertFilter
  ): Promise<boolean> => {
    this.isLoading = true
    try {
      return await contractEventService.exportConvertEvents(params)
    } finally {
      this.isLoading = false
    }
  }

  /** Feature-0384 — `false` = BE trả 204 (không có dòng nào) ⇒ scene hiện cảnh báo thay vì im lặng. */
  @action
  public exportSalesPolicyEvents = async (
    params: IExportContractEventConvertFilter
  ): Promise<boolean> => {
    this.isLoading = true
    try {
      return await contractEventService.exportSalesPolicyEvents(params)
    } finally {
      this.isLoading = false
    }
  }

  /**
   * Feature-0371 — xem trước tệp nhập sự kiện chuyển đổi. Truyền thẳng cho `onImport` của
   * `ReviewImportExcelV2`, nên PHẢI return `{ datas, errors }`.
   *
   * Feature-0382 — nhận thêm `kind` vì hai mục menu (DA qua SPA/LTL, thay đổi CSBH) dùng hai route xem
   * trước khác nhau; route mang sẵn loại sự kiện được phép nhập.
   */
  @action
  public validateBeforeImportConvert = async (
    kind: ContractEventImportKind,
    file: File
  ): Promise<{ datas: any[]; errors: any[] }> => {
    this.isLoading = true
    try {
      return await contractEventService.validateBeforeImportConvert(kind, file)
    } finally {
      this.isLoading = false
    }
  }

  // change payment term
  @action
  public exportPaymentTerm = async (body) => {
    this.isLoading = true
    await contractEventService
      .exportPaymentTerm(body)
      .finally(() => (this.isLoading = false))
  }

  @action
  public importPaymentTerm = async (contractId: number, file: File) => {
    this.isLoading = true
    try {
      const result = await contractEventService
        .importPaymentTerm(contractId, file)
        .finally(() => (this.isLoading = false))

      return result
    } catch (error) {
      this.isLoading = false
      throw error
    }
  }

  @action
  async emptyPrintData() {
    this.printReviewData = ""
  }

  @action
  async generatePrintDocument(body: any) {
    this.isLoadingPrint = true
    const result = await contractEventService
      .GeneratePrintDocument(body)
      .finally(() => {
        this.isLoadingPrint = false
      })

    this.printReviewData = result
    return result
  }

  @action
  async generatePrintDocumentTransfer(body: any) {
    this.isLoadingPrint = true
    const result = await contractEventService
      .GeneratePrintDocumentTransfer(body)
      .finally(() => {
        this.isLoadingPrint = false
      })
    this.printReviewData = result
    return result
  }

  @action
  async generatePrintDocumentLiquidation(body: any) {
    this.isLoadingPrint = true
    const result = await contractEventService
      .GeneratePrintDocumentLiquidation(body)
      .finally(() => {
        this.isLoadingPrint = false
      })
    this.printReviewData = result
    return result
  }



  @action
  async getContractsForSelection(params: any) {
    this.isLoadingContractSelection = true
    try {
      this.contractSelectionData = await contractService.getAll(params)
    } finally {
      this.isLoadingContractSelection = false
    }
  }

  @action
  initContractSelection() {
    this.contractSelectionData = { items: [], totalCount: 0 }
  }

  /**
   * Feature-0204 — tra đợt cấp sổ hồng của các hợp đồng đang chọn. Kết quả KHÔNG giữ trong store:
   * người dùng sửa từng dòng ngay trên form bulk nên state đó thuộc về form, giữ 2 nơi sẽ lệch.
   */
  @action
  async resolvePinkBookPhaseInfo(
    contractIds: number[]
  ): Promise<IPinkBookPhaseInfo[]> {
    this.isLoading = true
    try {
      return await contractEventService.resolvePinkBookPhaseInfo(contractIds)
    } finally {
      this.isLoading = false
    }
  }

  @action
  async createPinkBookDueDateEvents(
    body: IPinkBookDueDateBatchInput
  ): Promise<IContractEventFeeBatchResult> {
    this.isLoading = true
    try {
      return await contractEventService.createPinkBookDueDateEvents(body)
    } finally {
      this.isLoading = false
    }
  }

  @action
  async importContractEventExcel(kind: ContractEventImportKind, file: File) {
    this.isLoading = true
    try {
      return await contractEventService
        .importContractEventExcel(kind, file)
        .finally(() => (this.isLoading = false))
    } catch (error) {
      this.isLoading = false
      throw error
    }
  }

  @action
  async downloadContractEventImportTemplate(kind: ContractEventImportKind) {
    await contractEventService.downloadContractEventImportTemplate(kind)
  }
}

export default ContractEventStore
